import React from "react";
import { Link } from "react-router-dom";
import Slider from "../components/Slider";
import fabricationImage from "../images/custom_fab.png";
import plasmaImage from "../images/plasma.png";
import printingImage from "../images/3d_printing.png";
import laserImage from "../images/laser_cutting-v2.webp";
import prototypeImage from "../images/proto.png";
import cadImage from "../images/cad_design.png";

const specialistServices = [
  {
    title: "General Metal Fabrication",
    description:
      "Repairs, modifications and custom metalwork for commercial and one-off projects.",
    image: fabricationImage,
  },
  {
    title: "Plasma Cutting",
    description:
      "Profile cutting for larger steel parts, brackets, plates and fabrication projects.",
    image: plasmaImage,
    path: "/plasma-cutting",
  },
  {
    title: "Prototype Machining",
    description:
      "Turn an early idea into a practical test part, jig, fixture or working prototype.",
    image: prototypeImage,
  },
  {
    title: "CAD Design",
    description:
      "Drawings, modelling and design support to get your concept ready for manufacture.",
    image: cadImage,
  },
];

const Home = () => {
  return (
    <div className="space-y-20 pb-12">
      <section className="grid items-center gap-10 overflow-hidden rounded-3xl bg-slate-950 px-6 py-10 text-white shadow-2xl md:grid-cols-2 md:px-10 md:py-14">
        <div>
          <p className="text-sm font-bold uppercase tracking-[0.24em] text-orange-400">
            SEFF MachineWorks
          </p>
          <h1 className="mt-4 text-4xl font-black leading-tight text-white md:text-6xl">
            Need something made
            <span className="block text-orange-400">in metal or plastic?</span>
            <span className="mt-3 block text-2xl text-slate-300 md:text-4xl">
              Need something fixed in metal?
            </span>
          </h1>
          <p className="mt-6 max-w-xl text-lg leading-relaxed text-slate-300">
            Custom parts, brackets, repairs, signs, prototypes and one-off jobs
            in steel, aluminium, stainless steel and engineering plastics.
          </p>
          <p className="mt-5 max-w-xl border-l-4 border-orange-500 pl-4 font-bold text-white">
            We work with metal and plastics—not handyman repairs.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link
              to="/services"
              className="rounded-xl bg-orange-500 px-6 py-3 font-black text-slate-950 transition hover:bg-orange-400"
            >
              Explore our services
            </Link>
            <Link
              to="/contact"
              className="rounded-xl border border-white/20 px-6 py-3 font-bold text-white transition hover:border-white/40 hover:bg-white/10"
            >
              Talk about a project
            </Link>
          </div>
          <div className="mt-8 flex flex-wrap gap-x-6 gap-y-2 text-sm font-semibold text-slate-400">
            <span>✓ Metal fabrication &amp; repair</span>
            <span>✓ Custom laser &amp; plasma cutting</span>
            <span>✓ Functional 3D-printed parts</span>
          </div>
        </div>
        <div className="overflow-hidden rounded-2xl border border-white/10 bg-slate-900 shadow-xl">
          <Slider />
        </div>
      </section>

      <section>
        <div className="mx-auto max-w-3xl text-center">
          <p className="text-sm font-black uppercase tracking-[0.22em] text-orange-500">
            What we do
          </p>
          <h2 className="mt-3 text-3xl font-black text-slate-900 dark:text-white md:text-5xl">
            Manufacturing help, without the big-job minimums.
          </h2>
          <p className="mt-5 text-lg leading-relaxed text-slate-600 dark:text-slate-300">
            Start with an instant online estimate or talk to us about a metal
            repair, fabricated component, prototype or functional plastic part.
            One-off jobs and small quantities are welcome.
          </p>
        </div>

        <div className="mt-10 grid gap-6 lg:grid-cols-2">
          <Link
            to="/laser-cutting"
            className="group relative min-h-[390px] overflow-hidden rounded-3xl bg-slate-950 shadow-xl"
          >
            <img
              src={laserImage}
              alt="Precision metal laser cutting"
              className="absolute inset-0 h-full w-full object-cover transition duration-700 group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/55 to-transparent" />
            <div className="absolute inset-x-0 bottom-0 p-7 md:p-9">
              <div className="mb-4 flex items-center gap-3">
                <span className="rounded-full bg-orange-500 px-3 py-1 text-xs font-black tracking-wider text-slate-950">
                  NEW
                </span>
                <span className="text-sm font-bold text-orange-300">
                  Instant online estimate
                </span>
              </div>
              <h3 className="text-3xl font-black text-white">Laser Cutting</h3>
              <p className="mt-3 max-w-lg leading-relaxed text-slate-200">
                Upload a DXF, SVG or image for precision-cut signs, artwork,
                prototypes and custom metal parts.
              </p>
              <span className="mt-6 inline-flex items-center gap-2 font-black text-orange-400">
                Price your part{" "}
                <span className="transition group-hover:translate-x-1">→</span>
              </span>
            </div>
          </Link>

          <Link
            to="/3dprint"
            className="group relative min-h-[390px] overflow-hidden rounded-3xl bg-slate-950 shadow-xl"
          >
            <img
              src={printingImage}
              alt="Custom 3D printing"
              className="absolute inset-0 h-full w-full object-cover transition duration-700 group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/60 to-slate-950/5" />
            <div className="absolute inset-x-0 bottom-0 p-7 md:p-9">
              <div className="mb-4">
                <span className="text-sm font-bold text-cyan-300">
                  Interactive STL quote
                </span>
              </div>
              <h3 className="text-3xl font-black text-white">3D Printing</h3>
              <p className="mt-3 max-w-lg leading-relaxed text-slate-200">
                Preview your model, select the material and finish, and get an
                instant estimate for prototypes or functional parts.
              </p>
              <span className="mt-6 inline-flex items-center gap-2 font-black text-cyan-300">
                Upload your model{" "}
                <span className="transition group-hover:translate-x-1">→</span>
              </span>
            </div>
          </Link>
        </div>

        <div className="mt-6 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {specialistServices.map((service) => (
            <Link
              key={service.title}
              to={service.path || "/contact"}
              className="group overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm transition hover:-translate-y-1 hover:border-orange-300 hover:shadow-xl dark:border-slate-700 dark:bg-slate-800"
            >
              <div className="h-40 overflow-hidden bg-slate-200">
                <img
                  src={service.image}
                  alt={service.title}
                  className="h-full w-full object-cover transition duration-500 group-hover:scale-105"
                  loading="lazy"
                  decoding="async"
                />
              </div>
              <div className="p-5">
                <h3 className="text-lg font-black text-slate-900 dark:text-white">
                  {service.title}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-600 dark:text-slate-300">
                  {service.description}
                </p>
                <span className="mt-4 inline-flex items-center gap-2 text-sm font-black text-orange-600 dark:text-orange-400">
                  {service.path ? "Price your part" : "Discuss a project"}{" "}
                  <span className="transition group-hover:translate-x-1">
                    →
                  </span>
                </span>
              </div>
            </Link>
          ))}
        </div>

        <div className="mt-8 text-center">
          <Link
            to="/services"
            className="inline-flex items-center gap-2 rounded-xl border border-slate-300 px-6 py-3 font-bold text-slate-700 transition hover:border-orange-400 hover:text-orange-600 dark:border-slate-600 dark:text-slate-200"
          >
            View the full services page <span>→</span>
          </Link>
        </div>
      </section>

      <section className="overflow-hidden rounded-3xl bg-gradient-to-r from-orange-500 to-amber-400 px-6 py-10 text-slate-950 shadow-xl md:flex md:items-center md:justify-between md:px-10">
        <div>
          <p className="text-sm font-black uppercase tracking-[0.2em]">
            Not sure which process?
          </p>
          <h2 className="mt-2 text-3xl font-black">
            Show us what you need to make.
          </h2>
          <p className="mt-3 max-w-2xl font-medium text-slate-800">
            Send a sketch, drawing or photo of the metal or plastic part you
            need. We’ll help choose a practical manufacturing or metal-repair
            process.
          </p>
        </div>
        <Link
          to="/contact"
          className="mt-6 inline-flex shrink-0 rounded-xl bg-slate-950 px-6 py-3 font-black text-white transition hover:bg-slate-800 md:mt-0"
        >
          Start a conversation
        </Link>
      </section>
    </div>
  );
};

export default Home;
